import * as React from 'react';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { pressable, type MotionSafeProps } from '@/lib/motion';

/*
 * Restyled to ScreenAtlas tokens (web/docs/ui-ux.md §5). Three jobs, three
 * looks, no fourth pattern:
 * - `default` / `outline`: chips (pick among many, per-item state) — small
 *   radius, selected = neutral ink fill (selection is ink, never accent).
 * - `segmented`: the static raised thumb (`surface-raised` + the inset
 *   highlight) inside a ToggleGroup's recessed pill track — the one pill.
 * - `ink`: the gliding ink thumb (selection is state, never accent) — the
 *   active label inverts to `text-bg` over a transparent fill so the ink
 *   `motion.span` thumb (rendered by `toggle-group.tsx`) shows through.
 */
const toggleVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-subhead font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'rounded-sm bg-transparent text-ink-secondary hover:bg-surface-alt hover:text-ink data-[state=on]:bg-ink data-[state=on]:text-bg',
        outline:
          'rounded-sm border border-border bg-transparent text-ink-secondary hover:bg-surface-alt hover:text-ink data-[state=on]:bg-ink data-[state=on]:text-bg',
        segmented:
          'rounded-full bg-transparent text-ink-secondary hover:text-ink data-[state=on]:bg-surface-raised data-[state=on]:text-ink data-[state=on]:shadow-highlight',
        ink:
          'relative rounded-full bg-transparent text-ink-secondary hover:text-ink data-[state=on]:bg-transparent data-[state=on]:text-bg',
      },
      size: {
        default: 'h-9 min-w-9 px-3',
        sm: 'h-8 min-w-8 px-2.5',
        lg: 'h-10 min-w-10 px-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const MotionToggleRoot = motion.create(TogglePrimitive.Root);

type ToggleProps = MotionSafeProps<React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root>> &
  VariantProps<typeof toggleVariants>;

const Toggle = React.forwardRef<React.ElementRef<typeof TogglePrimitive.Root>, ToggleProps>(
  ({ className, variant, size, ...props }, ref) => (
  <MotionToggleRoot
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...pressable()}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

/* eslint-disable-next-line react-refresh/only-export-components */
export { Toggle, toggleVariants };
